import { useTheme } from "@emotion/react";
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { MdClose, MdOutlineUploadFile } from "react-icons/md";

export type AttachmentEntry = {
  fileName: string;
  fileType: string;
  contentBase64: string;
};

type Props = {
  attachments: AttachmentEntry[];
  onChange: (attachments: AttachmentEntry[]) => void;
};

const readFileAsBase64 = (file: File): Promise<AttachmentEntry> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result;
      if (typeof dataUrl !== "string") {
        reject(new Error("Failed to read file"));
        return;
      }
      const base64 = dataUrl.split(",")[1] ?? "";
      resolve({ fileName: file.name, fileType: file.type, contentBase64: base64 });
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

export const SupportAttachmentPicker: React.FC<Props> = ({ attachments, onChange }) => {
  const theme = useTheme();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const results = await Promise.allSettled(acceptedFiles.map(readFileAsBase64));
      const entries = results.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
      onChange([...attachments, ...entries]);
    },
    [attachments, onChange],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*,application/pdf",
    multiple: true,
  });

  const removeAttachment = (index: number) => {
    onChange(attachments.filter((_, i) => i !== index));
  };

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem", width: "100%" }}>
      <div
        css={{
          border: isDragActive ? `2px dashed ${theme.colors.primary}` : `2px dashed ${theme.colors.border}`,
          borderRadius: "8px",
          padding: "10px",
          textAlign: "center",
          transition: "border-color 0.15s",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div
          {...getRootProps()}
          css={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "0.5rem",
            padding: "1rem",
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <MdOutlineUploadFile size={40} css={{ color: isDragActive ? theme.colors.primary : theme.colors.primaryLight }} />
          <span
            css={{
              fontFamily: theme.displayFontFamily,
              fontSize: "14px",
              color: isDragActive ? theme.colors.primary : theme.colors.primary,
            }}
          >
            Przeciągnij i upuść lub <strong>dodaj załączniki</strong>
          </span>
        </div>
      </div>
      {attachments.length > 0 && (
        <div css={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
          {attachments.map((att, i) => (
            <div
              key={`${att.fileName}-${i}`}
              css={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                fontSize: "12px",
                background: theme.colors.primaryLight,
                color: theme.colors.primary,
                borderRadius: theme.extraSmallBorderRadius,
                padding: "0.25rem 0.5rem",
                maxWidth: "200px",
              }}
            >
              <span
                css={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {att.fileName}
              </span>
              <MdClose
                size={14}
                css={{ cursor: "pointer", flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeAttachment(i);
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
